<template>
  <!-- When we have a hint, show the chip with a tooltip -->
  <v-tooltip
    v-if="chipHint"
    :text="chipHint"
    location="right"
    :open-on-click="status === PaymentStatus.REQUIRES_CAPTURE"
  >
    <template v-slot:activator="{ props: tooltipProps }">
      <v-chip
        size="small"
        :color="chipInfo?.color || defaultChipInfo.color"
        :variant="chipInfo?.variant || defaultChipInfo.variant || 'tonal'"
        v-bind="{ ...props, ...tooltipProps }"
        :tabindex="status === PaymentStatus.REQUIRES_CAPTURE ? 0 : undefined"
        @click="stopAwaitingCaptureNavigation"
        :aria-label="
          status === PaymentStatus.REQUIRES_CAPTURE ? `${chipInfo.label}. ${chipHint}` : undefined
        "
      >
        <v-icon v-if="cart" icon="mdi-cart" class="mr-1" />
        <v-icon v-if="showFormIcon" icon="mdi-clipboard-text-outline" class="mr-1" />
        <span :class="chipInfo?.text || defaultChipInfo.text">{{
          chipInfo?.label || props.status
        }}</span>
        <v-icon
          v-if="chipInfo?.icon || defaultChipInfo.icon"
          :icon="chipInfo?.icon || defaultChipInfo.icon"
          class="ml-1"
        />
      </v-chip>
    </template>
  </v-tooltip>

  <!-- When we don't have a hint, just show the chip -->
  <v-chip
    v-else
    size="small"
    :color="chipInfo?.color || defaultChipInfo.color"
    :variant="chipInfo?.variant || defaultChipInfo.variant || 'tonal'"
    v-bind="props"
  >
    <v-icon v-if="cart" icon="mdi-cart" class="mr-1" />
    <v-icon v-if="showFormIcon" icon="mdi-clipboard-text-outline" class="mr-1" />
    <span :class="chipInfo?.text || defaultChipInfo.text">{{
      chipInfo?.label || props.status
    }}</span>
    <v-icon
      v-if="chipInfo?.icon || defaultChipInfo.icon"
      :icon="chipInfo?.icon || defaultChipInfo.icon"
      class="ml-1"
    />
  </v-chip>
</template>

<script lang="ts" setup>
import {
  TerminalStatus,
  PaymentStatus,
  UserRoleStatus,
  UserActiveStatus,
  TerminalFormStatus,
  CommonStatus,
} from "@/shared/enums/status.enum";
import i18n from "@/app/plugins/i18n";
import { computed, ref, watch } from "vue";
import { useAccountStore } from "@/account/stores/account";
import { db } from "@/app/plugins/firebase";
import { collection, getDocs, limit, query, where } from "firebase/firestore";

import { formatPaymentCaptureBefore } from "@/payments/utils/paymentStatus.utils";

const props = defineProps({
  status: {
    type: [String, Boolean],
    default: TerminalStatus.OFFLINE,
  },
  captureBefore: {
    type: Number,
    default: undefined,
  },
  errorMessage: {
    type: String,
  },
  partiallyRefundAmount: {
    type: Number,
  },
  cart: {
    type: Boolean,
    default: false,
  },
  form: {
    type: Boolean,
    default: false,
  },
  paymentId: {
    type: String,
    default: null,
  },
  intentStatus: {
    type: String,
    default: null,
  },
});

const formStatusCache = new Map<string, boolean>();
const pendingFormStatusRequests = new Map<string, Promise<boolean>>();

const fetchFormStatus = async (account: string, pid: string) => {
  const cacheKey = `${account}-${pid}`;

  if (formStatusCache.has(cacheKey)) {
    return formStatusCache.get(cacheKey) ?? false;
  }

  if (pendingFormStatusRequests.has(cacheKey)) {
    return pendingFormStatusRequests.get(cacheKey) ?? false;
  }

  const request = (async () => {
    try {
      const submissionsRef = collection(db, "accounts", account, "formCompleted");
      const submissionsQuery = query(submissionsRef, where("pid", "==", pid), limit(1));
      const snapshot = await getDocs(submissionsQuery);
      const hasSubmission = !snapshot.empty;
      formStatusCache.set(cacheKey, hasSubmission);
      return hasSubmission;
    } catch (error) {
      console.error("[StatusChip] Unable to read form submissions", error);
      formStatusCache.set(cacheKey, false);
      return false;
    } finally {
      pendingFormStatusRequests.delete(cacheKey);
    }
  })();

  pendingFormStatusRequests.set(cacheKey, request);
  return request;
};

const accountStore = useAccountStore();
const accountId = computed(() => accountStore.account?.id ?? null);
const hasCompletedForm = ref(false);
let requestToken = 0;

watch(
  () => [accountId.value, props.paymentId],
  async ([account, pid]) => {
    const currentToken = ++requestToken;
    hasCompletedForm.value = false;

    if (!account || !pid) {
      return;
    }

    const status = await fetchFormStatus(account, pid);
    if (currentToken === requestToken) {
      hasCompletedForm.value = status;
    }
  },
  { immediate: true },
);

const showFormIcon = computed(() => props.form || hasCompletedForm.value);

const resolvedStatus = computed(() => {
  const status = props.status;
  const isFormStatus =
    status === TerminalFormStatus.COMPLETE || status === TerminalFormStatus.FORM_INCOMPLETE;

  if (!isFormStatus) {
    return status;
  }

  const formIntentStatus = props.intentStatus || null;
  const intentCompletedStatuses = [PaymentStatus.SUCCEEDED, PaymentStatus.CANCELED];

  if (formIntentStatus && !intentCompletedStatuses.includes(formIntentStatus as PaymentStatus)) {
    return TerminalFormStatus.FORM_INCOMPLETE;
  }

  return status;
});

// Create a map for all the status configurations
const createStatusMap = () => {
  const map = new Map();

  // Terminal statuses
  map.set(TerminalStatus.OFFLINE, {
    color: "grey-lighten-2",
    label: i18n.global.t(`status.${TerminalStatus.OFFLINE}`),
    text: "text-grey-darken-3 text-caption",
  });
  map.set(TerminalStatus.INACTIVE, {
    color: "red",
    label: i18n.global.t(`status.${TerminalStatus.INACTIVE}`),
    text: "text-caption",
  });
  map.set(TerminalStatus.ACTIVE, {
    color: "green-lighten-1",
    label: i18n.global.t(`status.${TerminalStatus.ACTIVE}`),
    text: "text-caption",
  });
  map.set(TerminalStatus.ONLINE, {
    color: "green-lighten-1",
    label: i18n.global.t(`status.${TerminalStatus.ONLINE}`),
    text: "text-caption",
  });

  // Payment statuses
  map.set(PaymentStatus.SUCCEEDED, {
    color: "green-lighten-1",
    label: i18n.global.t(`status.${PaymentStatus.SUCCEEDED}`),
    text: "text-caption",
    icon: "mdi-check",
    hint: i18n.global.t("toolTipsMessage.succeeded"),
  });
  map.set(PaymentStatus.PAID, {
    color: "green-lighten-1",
    label: i18n.global.t(`status.${PaymentStatus.SUCCEEDED}`),
    text: "text-caption",
    icon: "mdi-check",
    hint: i18n.global.t("toolTipsMessage.succeeded"),
  });
  map.set(PaymentStatus.CANCELED, {
    color: "grey",
    label: i18n.global.t(`status.${PaymentStatus.CANCELED}`),
    text: "text-grey-darken-3 text-caption",
    icon: "mdi-close",
    hint: i18n.global.t("toolTipsMessage.canceled"),
  });
  map.set(PaymentStatus.FAILED, {
    color: "red-lighten-1",
    label: i18n.global.t(`status.${PaymentStatus.FAILED}`),
    text: "text-caption",
    icon: "mdi-close",
    hint: props.errorMessage,
  });
  map.set(PaymentStatus.PROCESSING, {
    color: "yellow-darken-2",
    label: i18n.global.t(`status.${PaymentStatus.PROCESSING}`),
    text: "text-grey-darken-3 text-caption",
    icon: "mdi-clock-time-five-outline",
    hint: i18n.global.t("toolTipsMessage.processing"),
  });
  map.set(PaymentStatus.REFUNDED, {
    color: "blue-darken-2",
    label: i18n.global.t(`status.${PaymentStatus.REFUNDED}`),
    text: "text-caption",
    icon: "mdi-arrow-u-left-top",
    hint: i18n.global.t("toolTipsMessage.refunded"),
  });
  map.set(PaymentStatus.PARTIALLY_REFUNDED, {
    color: "blue-darken-2",
    label: i18n.global.t(`status.${PaymentStatus.PARTIALLY_REFUNDED}`),
    text: "text-caption",
    icon: "mdi-arrow-u-left-top",
    hint: `$ ${props.partiallyRefundAmount} ${i18n.global.t("toolTipsMessage.partially_refunded")}`,
  });
  map.set(PaymentStatus.REQUIRES_ACTION, {
    color: "yellow-darken-2",
    label: i18n.global.t(`status.${PaymentStatus.REQUIRES_ACTION}`),
    text: "text-grey-darken-3 text-caption",
    icon: "mdi-clock-time-five-outline",
    hint: i18n.global.t("toolTipsMessage.requires_action"),
  });
  map.set(PaymentStatus.REQUIRES_PAYMENT_METHOD, {
    color: "yellow-darken-2",
    label: i18n.global.t(`status.${PaymentStatus.REQUIRES_PAYMENT_METHOD}`),
    text: "text-grey-darken-3 text-caption",
    icon: "mdi-clock-time-five-outline",
    hint: i18n.global.t("toolTipsMessage.requires_payment_method"),
  });
  map.set(PaymentStatus.INCOMPLETE, {
    color: "grey-lighten-2",
    label: i18n.global.t(`status.${PaymentStatus.INCOMPLETE}`),
    text: "text-grey-darken-3 text-caption",
    icon: "mdi-close",
    hint: i18n.global.t("toolTipsMessage.incomplete"),
  });
  map.set(PaymentStatus.REQUIRES_CONFIRMATION, {
    color: "grey-lighten-2",
    label: i18n.global.t(`status.${PaymentStatus.REQUIRES_CONFIRMATION}`),
    text: "text-grey-darken-3 text-caption",
    icon: "mdi-close",
    hint: i18n.global.t("toolTipsMessage.incomplete"),
  });

  map.set(PaymentStatus.REQUIRES_CAPTURE, {
    color: "amber-darken-3",
    label: i18n.global.t("status.requires_capture"),
    text: "text-caption",
    icon: "mdi-clock-outline",
  });

  // User role statuses
  map.set(UserRoleStatus.ADMIN, {
    color: "yellow-darken-2",
    label: i18n.global.t(`page.users.userRoles.${UserRoleStatus.ADMIN}`),
    text: "text-grey-darken-3",
  });
  map.set(UserRoleStatus.OWNER, {
    color: "grey-lighten-2",
    label: i18n.global.t(`page.users.userRoles.${UserRoleStatus.OWNER}`),
    text: "text-grey-darken-3",
  });
  map.set(UserRoleStatus.STAFF, {
    color: "green-lighten-1",
    label: i18n.global.t(`page.users.userRoles.${UserRoleStatus.STAFF}`),
    text: "",
  });

  // User active statuses
  map.set(UserActiveStatus.INVITED, {
    color: "yellow-darken-2",
    label: i18n.global.t(`status.${UserActiveStatus.INVITED}`),
    text: "text-grey-darken-3",
  });

  // Terminal form statuses
  map.set(TerminalFormStatus.ACTIVE, {
    color: "green-lighten-1",
    label: i18n.global.t(`status.${TerminalFormStatus.ACTIVE}`),
    text: "text-caption",
  });
  map.set(TerminalFormStatus.ARCHIVED, {
    color: "grey-darken-1",
    label: i18n.global.t(`status.${TerminalFormStatus.ARCHIVED}`),
    text: "",
  });
  map.set(TerminalFormStatus.COMPLETE, {
    color: "deep-purple-accent-2",
    label: i18n.global.t(`status.${TerminalFormStatus.COMPLETE}`),
    text: "",
    icon: "mdi-check",
    hint: i18n.global.t("toolTipsMessage.formCompleted"),
  });
  map.set(TerminalFormStatus.FORM_INCOMPLETE, {
    color: "deep-purple-accent-2",
    label: i18n.global.t("common.action.formOpen"),
    text: "text-deep-purple-accent-2",
    variant: "outlined",
    icon: "mdi-exclamation",
    hint: i18n.global.t("toolTipsMessage.formOpen"),
  });

  // Common statuses - these are the standardized ones to use for active/inactive
  map.set(CommonStatus.ACTIVE, {
    color: "green",
    label: i18n.global.t("common.action.active"),
    text: "text-caption",
  });
  map.set(CommonStatus.INACTIVE, {
    color: "red",
    label: i18n.global.t("common.action.inactive"),
    text: "text-caption",
  });

  // String literal active/inactive support (for legacy usage)
  map.set("Active", {
    color: "green",
    label: i18n.global.t("common.action.active"),
    text: "text-caption",
  });
  map.set("Inactive", {
    color: "red",
    label: i18n.global.t("common.action.inactive"),
    text: "text-caption",
  });

  // Enabled/disabled statuses
  map.set("Enabled", {
    color: "green",
    label: i18n.global.t("common.status.enabled"),
    text: "text-caption",
  });
  map.set("Disabled", {
    color: "red",
    label: i18n.global.t("common.status.disabled"),
    text: "text-caption",
  });

  // Boolean values
  map.set(true, {
    color: "green",
    label: i18n.global.t("common.action.active"),
    text: "text-caption",
  });
  map.set(false, {
    color: "red",
    label: i18n.global.t("common.action.inactive"),
    text: "text-caption",
  });

  return map;
};

const statusMap = createStatusMap();

const defaultChipInfo = computed(() => {
  // Default fallback values if status is not found in statusMap
  return {
    color: "grey-lighten-2",
    label:
      typeof props.status === "boolean"
        ? props.status
          ? i18n.global.t("common.action.active")
          : i18n.global.t("common.action.inactive")
        : props.status,
    text: "text-grey-darken-3 text-caption",
    variant: "tonal",
    icon: undefined,
  };
});

const stopAwaitingCaptureNavigation = (event: Event) => {
  if (props.status === PaymentStatus.REQUIRES_CAPTURE) event.stopPropagation();
};

const chipHint = computed(() => {
  if (props.status === PaymentStatus.REQUIRES_CAPTURE) {
    const deadline = formatPaymentCaptureBefore(props.captureBefore);
    return deadline
      ? i18n.global.t("page.paymentDetails.authorization.captureBeforeValue", { date: deadline })
      : i18n.global.t("page.paymentDetails.authorization.deadlineUnavailable");
  }
  return chipInfo.value?.hint;
});

const chipInfo = computed(() => {
  return statusMap.get(resolvedStatus.value) || defaultChipInfo.value;
});
</script>
